import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface Recording {
  id: string;
  name: string;
  meeting_id: string;
  meeting_title?: string;
  created_at: string;
  size: number;
  url?: string;
}

export const useRecordings = (meetingId?: string) => {
  const [recordings, setRecordings] = useState<Recording[]>([]);
  const [loading, setLoading] = useState(true);
  const [storageUsed, setStorageUsed] = useState(0);
  const { toast } = useToast();

  useEffect(() => {
    fetchRecordings();
  }, [meetingId]);

  const fetchRecordings = async () => {
    setLoading(true);
    try {
      // Fetch recordings from database with meeting info
      let query = supabase
        .from('recordings')
        .select(`
          id,
          meeting_id,
          file_name,
          file_path,
          file_size,
          duration,
          format,
          status,
          created_at,
          meetings (
            title
          )
        `)
        .order('created_at', { ascending: false });

      // Filter by meeting ID if provided
      if (meetingId) {
        query = query.eq('meeting_id', meetingId);
      }

      const { data: dbRecordings, error: dbError } = await query;

      if (dbError) throw dbError;

      // Transform and fetch signed URLs
      const allRecordings: Recording[] = [];
      let totalSize = 0;

      for (const rec of dbRecordings || []) {
        totalSize += rec.file_size || 0;

        // Get signed URL for playback
        const { data: urlData } = await supabase
          .storage
          .from('meeting-recordings')
          .createSignedUrl(rec.file_path, 3600); // 1 hour expiry

        allRecordings.push({
          id: rec.id,
          name: rec.file_name,
          meeting_id: rec.meeting_id,
          meeting_title: rec.meetings?.title || 'Unknown Meeting',
          created_at: rec.created_at,
          size: rec.file_size,
          url: urlData?.signedUrl
        });
      }

      setRecordings(allRecordings);
      setStorageUsed(totalSize);
    } catch (error: any) {
      console.error('Error fetching recordings:', error);
      toast({
        title: "Error",
        description: "Failed to load recordings",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const deleteRecording = async (recordingId: string, filePath: string) => {
    try {
      // Delete from storage
      const { error: storageError } = await supabase
        .storage
        .from('meeting-recordings')
        .remove([filePath]);

      if (storageError) throw storageError;

      // Delete from database
      const { error: dbError } = await supabase
        .from('recordings')
        .delete()
        .eq('id', recordingId);

      if (dbError) throw dbError;

      toast({
        title: "Recording deleted",
        description: "Recording has been successfully deleted"
      });

      fetchRecordings();
    } catch (error: any) {
      console.error('Error deleting recording:', error);
      toast({
        title: "Error",
        description: "Failed to delete recording",
        variant: "destructive"
      });
    }
  };

  const downloadRecording = async (filePath: string, fileName: string) => {
    try {
      const { data, error } = await supabase
        .storage
        .from('meeting-recordings')
        .download(filePath);

      if (error) throw error;

      // Create blob URL and trigger download
      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast({
        title: "Download started",
        description: "Recording download has started"
      });
    } catch (error: any) {
      console.error('Error downloading recording:', error);
      toast({
        title: "Error",
        description: "Failed to download recording",
        variant: "destructive"
      });
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  return {
    recordings,
    loading,
    storageUsed,
    formatFileSize,
    deleteRecording,
    downloadRecording,
    refreshRecordings: fetchRecordings
  };
};
